import { ReactElement } from 'react'
import { Select, SelectItem, NumberField, DatePicker } from '@calabriocloud/beacon-ui'
import { useTranslation } from 'react-i18next'
import { FormSection, FormHeader } from './layout'
import { contactLevel, timeType as timeTypeOptions, actionKey } from '../../constants'
import { convertDateToString, convertDateToDateValue, convertDateToDateTimeValue } from '../../util'
import { ErrorMessage, FormikErrors, FormikTouched, useFormikContext } from 'formik'
import { Goal } from '../../constants'
import { UserAssignment } from './contactAssignment/UserAssignment'

interface ContactAssignmentSectionProps {
  values: Goal,
  errors: FormikErrors<Goal>,
  touched: FormikTouched<Goal>,
  handleChange: (e: React.ChangeEvent<Element>) => void,
  handleBlur: (e: React.FocusEvent<Element>) => void,
}

export const ContactAssignmentSection = ({ handleBlur, errors, touched }: ContactAssignmentSectionProps): ReactElement => {
  const { t } = useTranslation()

  const formik = useFormikContext<Goal>()
  const { goalQuantity, whoType, timeType, effectiveStartDate, effectiveEndDate, dueDate, assigneeToWhoIds, contactGoalActionId } = formik.values
  const { isDisabled } = formik.status
  const handleGoalFrequencySelectChange = (value: number) => {
    formik.setFieldValue('timeType', value)
  }

  const handleContactLevelSelectChange = (value: number) => {
    formik.setFieldValue('whoType', value)
  }

  const getDuration = (timeType: number): string => {
    const durationMap: { [key: number]: () => string } = {
      1: () => t('contactsPerAssigneeAsDaily', { perAssigneeContacts: goalQuantity }),
      2: () => t('contactsPerAssigneeAsWeekly', { perAssigneeContacts: goalQuantity }),
      3: () => t('contactsPerAssigneeAsMonthly', { perAssigneeContacts: goalQuantity }),
      4: () => t('contactsPerAssigneeAsOnce', { perAssigneeContacts: goalQuantity, date: new Date(dueDate || '').toLocaleString() }),
      5: () => t('contactsPerAssigneeAsRange', { perAssigneeContacts: goalQuantity, startdate: new Date(effectiveStartDate || '').toLocaleDateString(), enddate: effectiveEndDate ? new Date(effectiveEndDate).toLocaleDateString() : '' }),
    }
    return durationMap[timeType]?.() || t('contactsPerAssignee', { perAssigneeContacts: goalQuantity })
  }

  const contactsInTotal = assigneeToWhoIds && Object.keys(assigneeToWhoIds).length > 0
    ? Object.entries(assigneeToWhoIds).reduce((sum: number, [_, whoIds]) => sum + (whoIds?.length || 0), 0)
    : 0
  return (
    <FormSection>
      <FormHeader title={t('contactAssignment')} />
      <div className='py-4'>
        <h4 className="mb-1 text-xl">{t('numberOfContactsLabelv2')}</h4>
        <p className="text-sm mb-4">{t('goalAdminContactsDescription')}</p>
        <NumberField isRequired className='w-20' data-testid='goal-quantity-input' aria-hidden="true" name='goalQuantity' isDisabled={isDisabled} minValue={1} maxValue={100} value={goalQuantity} onChange={(value: number) => formik.setFieldValue('goalQuantity', value)} />
      </div>
      <div className='py-4'>
        <h4 className="mb-1 text-xl">{t('goalLevel')}</h4>
        <p className="text-sm mb-4">{t('goalLevelDescription')}</p>
        <Select label={t('level')} className='w-80' data-testid='level-select' id='whoType' name='whoType' isDisabled={isDisabled}  onBlur={handleBlur} items={contactLevel} onSelectionChange={handleContactLevelSelectChange} selectedKey={whoType}>
          {item => (<SelectItem key={item.id} id={item.id}>{t(item.name)}</SelectItem>)}
        </Select>
        <ErrorMessage name='whoType'>
          {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
        </ErrorMessage>
      </div>
      <div className='py-4'>
        <h4 className='mb-1 text-xl'>{t('activity')}</h4>
        <p className='text-sm mb-4'>{t('goalAdminActivityDescription')}</p>
        <Select
          label={t('activity')}
          className='w-80'
          data-testid="goal-activity-select"
          // isRequired
          id='contactGoalActionId'
          name='contactGoalActionId'
          isDisabled={isDisabled}
          selectedKey={contactGoalActionId}
          items={actionKey.map((value, index) => ({ id: index, name: value }))}
          onBlur={handleBlur}
          onSelectionChange={(key: number) => {
            formik.setFieldValue('contactGoalActionId', key)
          }}
        >
          {item => (<SelectItem key={item.name} id={item.id}>{t(item.name)}</SelectItem>)}
        </Select>
        <ErrorMessage name='contactGoalActionId'>
          {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
        </ErrorMessage>
      </div>
      <UserAssignment id="assigneeToWhoIds" name="assigneeToWhoIds"  />
      <div className='py-4'>
        <h4 className='mb-1 text-xl'>{t('goalFrequency')}</h4>
        <p className="text-sm mb-4">{t('goalDurationDescription')}</p>
        <div className='flex gap-10'>
          <div>
            <Select label={t('duration')} className='w-80' data-testid='duration-select' id='timeType' name='timeType' isDisabled={isDisabled}  items={timeTypeOptions} selectedKey={timeType} onBlur={handleBlur}
              onSelectionChange={handleGoalFrequencySelectChange}>
              {item => (<SelectItem key={item.id} id={item.id}>{t(item.name)}</SelectItem>)}
            </Select>
            <ErrorMessage name='timeType'>
              {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
            </ErrorMessage>
          </div>
          {timeType === 4 && <DatePicker granularity='minute' label={t('selectDate')} name='dueDate' className='w-40' data-testid='select-date-input' isDisabled={isDisabled} isRequired value={convertDateToDateTimeValue(dueDate)} onChange={value => formik.setFieldValue('dueDate', convertDateToString(value))}></DatePicker>}
          {timeType === 5 && <div className='flex gap-20'>
            <DatePicker
              label={t('effectiveStartDate')}
              name='effectiveStartDate'
              className='w-40'
              data-testid='effective-start-date-input'
              isDisabled={isDisabled}
              errorMessage={errors.effectiveStartDate}
              isInvalid={errors.effectiveStartDate !== undefined && touched.effectiveStartDate}
              value={convertDateToDateValue(effectiveStartDate)}
              onChange={value => formik.setFieldValue('effectiveStartDate', convertDateToString(value))}>
            </DatePicker>
            <DatePicker
              label={t('effectiveEndDate')}
              name='effectiveEndDate'
              className='w-40'
              data-testid='effective-end-date-input'
              isDisabled={isDisabled}
              errorMessage={errors.effectiveEndDate}
              isInvalid={errors.effectiveEndDate !== undefined && touched.effectiveEndDate}
              value={convertDateToDateValue(effectiveEndDate)}
              onChange={value => formik.setFieldValue('effectiveEndDate', convertDateToString(value))}>
            </DatePicker>
          </div>}
        </div>
      </div>
      <div className='py-4'>
        <h4 className='mb-1 text-xl'>{t('contactAssignmentSummary')}</h4>
        <p className="text-sm">{t('contactsInTotal', { totalContacts: contactsInTotal * goalQuantity })}</p>
        <p className="text-sm">{getDuration(timeType ?? -1)}</p>
      </div>
    </FormSection>
  )
}
